import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUpIcon, TrendingDownIcon, AlertCircleIcon, CheckCircleIcon, InfoIcon } from 'lucide-react';
interface VeriScoreCardProps {
  score: number;
  propertyName: string;
  location: string;
  insights: {
    rentalYield: number;
    appreciation: number;
    infrastructureScore: number;
    demandLevel: 'High' | 'Medium' | 'Low';
  };
  trends: {
    label: string;
    value: string;
    positive: boolean;
  }[];
}
const VeriScoreCard: React.FC<VeriScoreCardProps> = ({
  score,
  propertyName,
  location,
  insights,
  trends
}) => {
  const getScoreColor = (score: number) => {
    if (score >= 8) return 'from-green-500 to-emerald-600';
    if (score >= 6) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-rose-600';
  };
  const getScoreLabel = (score: number) => {
    if (score >= 8) return 'Excellent';
    if (score >= 6) return 'Good';
    return 'Fair';
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-2xl shadow-lg overflow-hidden" data-id="element-746">
      {/* Header with Score */}
      <div className={`bg-gradient-to-r ${getScoreColor(score)} p-6 text-white`} data-id="element-747">
        <div className="flex items-start justify-between mb-4" data-id="element-748">
          <div data-id="element-749">
            <h3 className="text-2xl font-bold mb-1" data-id="element-750">{propertyName}</h3>
            <p className="text-white/80" data-id="element-751">{location}</p>
          </div>
          <div className="text-right" data-id="element-752">
            <div className="text-5xl font-bold" data-id="element-753">{score}</div>
            <div className="text-sm text-white/80" data-id="element-754">/ 10</div>
          </div>
        </div>
        <div className="flex items-center space-x-2" data-id="element-755">
          <CheckCircleIcon className="w-5 h-5" data-id="element-756" />
          <span className="font-medium" data-id="element-757">{getScoreLabel(score)} Investment</span>
        </div>
      </div>

      {/* Insights Grid */}
      <div className="p-6 grid grid-cols-2 gap-4" data-id="element-758">
        <div className="bg-blue-50 rounded-xl p-4" data-id="element-759">
          <div className="flex items-center justify-between mb-2" data-id="element-760">
            <span className="text-sm text-gray-600" data-id="element-761">Rental Yield</span>
            <InfoIcon className="w-4 h-4 text-gray-400" data-id="element-762" />
          </div>
          <div className="text-2xl font-bold text-blue-600" data-id="element-763">
            {insights.rentalYield}%
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-764">Annual return</div>
        </div>

        <div className="bg-green-50 rounded-xl p-4" data-id="element-765">
          <div className="flex items-center justify-between mb-2" data-id="element-766">
            <span className="text-sm text-gray-600" data-id="element-767">Appreciation</span>
            <TrendingUpIcon className="w-4 h-4 text-green-600" data-id="element-768" />
          </div>
          <div className="text-2xl font-bold text-green-600" data-id="element-769">
            +{insights.appreciation}%
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-770">3-year forecast</div>
        </div>

        <div className="bg-purple-50 rounded-xl p-4" data-id="element-771">
          <div className="flex items-center justify-between mb-2" data-id="element-772">
            <span className="text-sm text-gray-600" data-id="element-773">Infrastructure</span>
            <InfoIcon className="w-4 h-4 text-gray-400" data-id="element-774" />
          </div>
          <div className="text-2xl font-bold text-purple-600" data-id="element-775">
            {insights.infrastructureScore}/10
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-776">Development score</div>
        </div>

        <div className="bg-orange-50 rounded-xl p-4" data-id="element-777">
          <div className="flex items-center justify-between mb-2" data-id="element-778">
            <span className="text-sm text-gray-600" data-id="element-779">Demand</span>
            <AlertCircleIcon className="w-4 h-4 text-gray-400" data-id="element-780" />
          </div>
          <div className="text-2xl font-bold text-orange-600" data-id="element-781">
            {insights.demandLevel}
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-782">Market activity</div>
        </div>
      </div>

      {/* Trends */}
      <div className="px-6 pb-6" data-id="element-783">
        <h4 className="font-semibold mb-3 text-gray-800" data-id="element-784">Key Trends</h4>
        <div className="space-y-2" data-id="element-785">
          {trends.map((trend, index) => <motion.div key={index} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: index * 0.1
        }} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-786">
              <span className="text-sm text-gray-700" data-id="element-787">{trend.label}</span>
              <div className="flex items-center space-x-2" data-id="element-788">
                <span className="text-sm font-medium" data-id="element-789">{trend.value}</span>
                {trend.positive ? <TrendingUpIcon className="w-4 h-4 text-green-600" data-id="element-790" /> : <TrendingDownIcon className="w-4 h-4 text-red-600" data-id="element-791" />}
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* AI Recommendation */}
      <div className="px-6 pb-6" data-id="element-792">
        <div className="bg-gradient-to-r from-[#FF4E00] to-[#FF6B2C] rounded-xl p-4 text-white" data-id="element-793">
          <div className="flex items-start space-x-3" data-id="element-794">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-795">
              <CheckCircleIcon className="w-5 h-5" data-id="element-796" />
            </div>
            <div data-id="element-797">
              <h5 className="font-semibold mb-1" data-id="element-798">AI Recommendation</h5>
              <p className="text-sm text-white/90" data-id="element-799">
                {score >= 8 ? 'Strong buy opportunity. This property shows excellent growth potential with solid infrastructure development and high rental demand.' : score >= 6 ? 'Good investment option. Consider for medium-term holding with steady appreciation expected.' : 'Exercise caution. Review market conditions and consider alternative locations for better returns.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
};
export default VeriScoreCard;